import { useState, type ReactElement } from "react";

import type { Photo } from "../../api/types";
import { longDate } from "../../lib/date";
import { median } from "../../lib/math";

// A browsable library of the participant's already-submitted photos, grouped
// into collapsible date folders (newest first). The folder date is when the
// photo was taken (taken_at), falling back to the upload date. Per-photo FaceAge
// is shown only for results-visible studies.

function dateKey(p: Photo): string {
  return (p.taken_at ?? p.uploaded_at).slice(0, 10);
}

interface Folder {
  key: string;
  photos: Photo[];
}

function groupByDate(photos: Photo[]): Folder[] {
  const map = new Map<string, Photo[]>();
  for (const p of photos) {
    const k = dateKey(p);
    const bucket = map.get(k);
    if (bucket) bucket.push(p);
    else map.set(k, [p]);
  }
  return [...map.entries()]
    .map(([key, ps]) => ({ key, photos: ps }))
    .sort((a, b) => b.key.localeCompare(a.key)); // newest first
}

// How many date folders render before the rest hide behind "Show older photos"
// — ~1-2 weeks of daily submissions. Older folders stay one click away in
// batches of the same size; nothing is ever unavailable, only deferred.
const FOLDERS_PER_PAGE = 8;

// The folder-level FaceAge summary is the MEDIAN of the round's estimates
// (robust to a single bad detection), rounded to one decimal; null until any
// photo in the group has an estimate. Shared with the Daily Log's folders so
// both summaries agree.
export function medianFaceAge(photos: Photo[]): number | null {
  return median(
    photos
      .map((p) => p.estimate?.estimated_age)
      .filter((a): a is number => typeof a === "number"),
  );
}

// One thumbnail + its caption inside a folder grid — shared with the Daily Log's
// per-day folders so photo cells look identical everywhere.
export function FolderPhoto({
  photo,
  alt,
  resultsVisible,
}: {
  photo: Photo;
  alt: string;
  resultsVisible: boolean;
}): ReactElement {
  return (
    <figure className="folder-photo">
      {photo.download_url ? (
        <img className="thumb" src={photo.thumb_url ?? photo.download_url} alt={alt} loading="lazy" />
      ) : (
        <span className="thumb thumb-empty" />
      )}
      <figcaption className="folder-photo-meta">
        {resultsVisible && photo.estimate ? (
          <span className="folder-photo-age">{photo.estimate.estimated_age} yrs</span>
        ) : null}
        <span className={`status-pill ${photo.status}`}>{photo.status}</span>
      </figcaption>
    </figure>
  );
}

export function PhotoLibrary({
  photos,
  resultsVisible,
}: {
  photos: Photo[];
  resultsVisible: boolean;
}): ReactElement | null {
  const folders = groupByDate(photos);
  // Open the most recent folder by default so there's something to see on first
  // view; the rest stay collapsed.
  const [open, setOpen] = useState<Set<string>>(() => {
    const newest = folders[0]?.key;
    return newest ? new Set([newest]) : new Set();
  });
  const [visibleCount, setVisibleCount] = useState(FOLDERS_PER_PAGE);

  if (folders.length === 0) return null;

  const visibleFolders = folders.slice(0, visibleCount);
  const hiddenCount = folders.length - visibleFolders.length;

  function toggle(key: string): void {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="photo-library">
      <h3 className="library-title">Your photos</h3>
      {/* Keep the "7 days" copy in sync with FACEAGE_ARCHIVE_HOT_DAYS
          (core/config.py) — photos past it are archived and leave this view. */}
      <p className="muted">
        Photos stay viewable here for 7 days after upload; older photos are archived securely by
        the study team.
      </p>
      <ul className="folder-list">
        {visibleFolders.map((f) => {
          const isOpen = open.has(f.key);
          const panelId = `folder-${f.key}`;
          return (
            <li key={f.key} className="photo-folder">
              <button
                type="button"
                className="folder-head"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(f.key)}
              >
                <i
                  className={`fas fa-folder${isOpen ? "-open" : ""} folder-icon`}
                  aria-hidden="true"
                />
                <span className="folder-name">{longDate(f.key)}</span>
                {resultsVisible ? (
                  <span
                    className={`daily-faceage${medianFaceAge(f.photos) == null ? " pending" : ""}`}
                  >
                    {medianFaceAge(f.photos) == null
                      ? "FaceAge pending"
                      : `FaceAge ${medianFaceAge(f.photos)}`}
                  </span>
                ) : null}
                <span className="folder-count">
                  {f.photos.length} photo{f.photos.length === 1 ? "" : "s"}
                </span>
                <i
                  className={`fas fa-chevron-${isOpen ? "down" : "right"} folder-caret`}
                  aria-hidden="true"
                />
              </button>
              {isOpen ? (
                <div className="folder-grid" id={panelId}>
                  {f.photos.map((p) => (
                    <FolderPhoto
                      key={p.id}
                      photo={p}
                      alt={`Photo from ${longDate(f.key)}`}
                      resultsVisible={resultsVisible}
                    />
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="btn btn-secondary folder-show-older"
          onClick={() => setVisibleCount((n) => n + FOLDERS_PER_PAGE)}
        >
          Show older photos ({hiddenCount} more day{hiddenCount === 1 ? "" : "s"})
        </button>
      ) : null}
    </div>
  );
}
